import { ENDPOINTS } from 'config/endpoints';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { useConfirmModal } from 'context/confirmModal';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { MutationMeta, withMeta } from 'crono-fe-common/types/mutationMeta';
import { invalidateTimelineQueries } from '../timeline/useSearchTimeline';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';
import useNonOwnedSequenceActionGuard from 'hooks/strategy/useNonOwnedSequenceActionGuard';

export type StopSequenceInstanceInputs = {
  sequenceInstanceId: number;
  prospectId: string;
  strategyId: number;
  //Stops the sequence acting as the contact owner, without changing ownership. Manager only
  onBehalf?: boolean;
};

export default function useStopSequenceInstance() {
  const queryClient = useQueryClient();
  const { isManager } = useRoleCapabilities();

  const stopSequenceInstance = ({
    strategyId,
    onBehalf,
    ...data
  }: StopSequenceInstanceInputs) => {
    const request: Request = {
      url: `${ENDPOINTS.strategy.prospect}`,
      config: {
        method: 'delete',
        data: {
          ...data,
          //The flag is omitted rather than sent as false: non-managers sending it at all are rejected with 403
          ...(onBehalf && isManager && { onBehalf: true }),
        },
      },
    };
    return client(request);
  };

  const { data, mutate, ...rest } = useMutation<
    Response<null>,
    CronoError,
    StopSequenceInstanceInputs
  >(stopSequenceInstance, {
    onSettled: (_r, _e, variables) => {
      queryClient.invalidateQueries([
        ENDPOINTS.strategy.resumableCount,
        variables.strategyId,
      ]);
      queryClient.invalidateQueries([ENDPOINTS.account.search.main]);
      queryClient.invalidateQueries([ENDPOINTS.prospect.main]);
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.toString().startsWith(ENDPOINTS.prospect.search.main),
      });
      queryClient.invalidateQueries([ENDPOINTS.task.search]);
      queryClient.invalidateQueries([ENDPOINTS.task.main]);
      queryClient.invalidateQueries([ENDPOINTS.task.user.analytics]);
      queryClient.invalidateQueries([ENDPOINTS.task.aggregate]);

      queryClient.invalidateQueries([ENDPOINTS.task.total]);
      queryClient.invalidateQueries([ENDPOINTS.task.dailyQuantity]);
      queryClient.invalidateQueries([ENDPOINTS.strategy.main]);
      queryClient.invalidateQueries([ENDPOINTS.strategy.prospect]);
      queryClient.invalidateQueries([ENDPOINTS.strategy.search]);

      queryClient.invalidateQueries([ENDPOINTS.prospect.search.strategy]);
      queryClient.invalidateQueries([ENDPOINTS.strategy.details]);
      queryClient.invalidateQueries([ENDPOINTS.task.linkedin.quantity]);

      queryClient.invalidateQueries([ENDPOINTS.task.total]);
      queryClient.invalidateQueries([ENDPOINTS.task.dailyQuantity]);
      queryClient.invalidateQueries([ENDPOINTS.signal.search]);
      invalidateTimelineQueries(queryClient);
    },
  });

  return { data, mutate, ...rest };
}

export const useStopSequenceWithConfirm = (
  onSuccess?: () => void,
  meta?: MutationMeta,
) => {
  const { openModal: openConfirmModal } = useConfirmModal();
  const { openNonOwnedSequenceActionModal } = useNonOwnedSequenceActionGuard();
  const { mutate: stopSequenceInstance, ...rest } = useStopSequenceInstance();
  const { isManager } = useRoleCapabilities();

  const stopSequenceWithConfirm = (task: TaskTodo) => {
    const { sequenceInstanceId, prospectId, sequenceInstance } = task ?? {};

    if (!sequenceInstanceId || !prospectId || !sequenceInstance?.strategyId) {
      console.error('Invalid task state', task);
      return;
    }

    /**
     * The API checks the *contact*, so a task on a contact owned by someone
     * else needs the flag even when the sequence itself is the caller's, which
     * is what a reassigned contact leaves behind. Derived here rather than at
     * the call sites, which only ever hold the task.
     */
    const isNotOwned =
      task.prospect?.owned === false || sequenceInstance.owned === false;
    //Only a manager can stop on the owner's behalf, so only a manager gets the
    //confirmation that says so
    const canActOnBehalf = isNotOwned && isManager;

    const stopSequence = () =>
      stopSequenceInstance(
        withMeta(
          {
            sequenceInstanceId: sequenceInstanceId,
            prospectId: prospectId,
            strategyId: sequenceInstance.strategyId,
            onBehalf: canActOnBehalf,
          },
          meta ?? {},
        ),
        { onSuccess },
      );

    if (canActOnBehalf) {
      openNonOwnedSequenceActionModal('stop', stopSequence);
      return;
    }

    openConfirmModal({
      title: 'Are you sure you want to stop this sequence?',
      text: 'The sequence will be stopped for this contact',
      cancelText: 'No',
      confirmText: 'Yes',
      confirmFunction: stopSequence,
      cancelFunction: () => null,
    });
  };

  return { stopSequenceWithConfirm, ...rest };
};
